// src/features/bom/components/RecordWastageModal.tsx

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import {
    recordWastage,
    ProductionFloor,
} from '../../services/productionFloorApi';
import { searchProducts, ProductResult } from '@/features/pos/services/posApi';
import {
    X, AlertTriangle, Search, Package, DollarSign,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface RecordWastageModalProps {
    isOpen: boolean;
    onClose: () => void;
    floor: ProductionFloor;
}

const WASTAGE_TYPES = [
    { value: 'raw_material', label: 'Raw Material', description: 'Ingredient spoiled before use' },
    { value: 'production_loss', label: 'Production Loss', description: 'Lost during cooking/production' },
    { value: 'spoilage', label: 'Spoilage', description: 'Expired or gone bad' },
    { value: 'unsold', label: 'Unsold', description: 'Finished product not sold' },
    { value: 'overproduction', label: 'Overproduction', description: 'Made too much, discarded' },
];

export function RecordWastageModal({ isOpen, onClose, floor }: RecordWastageModalProps) {
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [wastageType, setWastageType] = useState('spoilage');
    const [productSearch, setProductSearch] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<ProductResult | null>(null);
    const [quantity, setQuantity] = useState('');
    const [uomId, setUomId] = useState('');
    const [uomName, setUomName] = useState('');
    const [costValue, setCostValue] = useState('');
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Search products
    const { data: productResults } = useQuery({
        queryKey: ['wastage-product-search', user?.tenantId, productSearch],
        queryFn: () => searchProducts(user!.tenantId, productSearch),
        enabled: !!user?.tenantId && productSearch.length >= 2,
    });

    const selectProduct = (product: ProductResult) => {
        setSelectedProduct(product);
        setProductSearch(product.name || '');
        setShowProductDropdown(false);

        const baseUom = product.uoms?.find(u => u.isBaseUom) || product.uoms?.[0];
        if (baseUom) {
            setUomId(baseUom.id);
            setUomName(baseUom.abbreviation || baseUom.name || 'unit');
        }
        if (product.costPrice) {
            setCostValue(product.costPrice);
        }
    };

    const handleSubmit = async () => {
        if (!selectedProduct) {
            addToast({ type: 'error', title: 'Product Required', message: 'Select a product.' });
            return;
        }
        if (!quantity || parseFloat(quantity) <= 0) {
            addToast({ type: 'error', title: 'Invalid Quantity', message: 'Enter a valid quantity.' });
            return;
        }
        if (!reason.trim()) {
            addToast({ type: 'error', title: 'Reason Required', message: 'Provide a reason for wastage.' });
            return;
        }

        setIsSubmitting(true);
        try {
            await recordWastage(user!.tenantId, floor.id, {
                wastageType,
                productId: selectedProduct.id,
                quantity,
                uomId,
                costValue: costValue || undefined,
                reason: reason.trim(),
                notes: notes || undefined,
            });

            queryClient.invalidateQueries({ queryKey: ['floor-wastage', floor.id] });
            queryClient.invalidateQueries({ queryKey: ['floor-inventory', floor.id] });
            queryClient.invalidateQueries({ queryKey: ['floor-dashboard', floor.id] });

            addToast({ type: 'success', title: 'Wastage Recorded', message: `${quantity} ${uomName} of ${selectedProduct.name} logged.` });
            onClose();
        } catch (err: any) {
            addToast({ type: 'error', title: 'Failed', message: err?.message || 'Could not record wastage.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setWastageType('spoilage');
        setProductSearch('');
        setSelectedProduct(null);
        setQuantity('');
        setUomId('');
        setUomName('');
        setCostValue('');
        setReason('');
        setNotes('');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const estimatedCost = selectedProduct && quantity
        ? parseFloat(quantity) * parseFloat(costValue || selectedProduct.costPrice || '0')
        : 0;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={handleClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-rose-500 to-red-600 px-6 py-4 rounded-t-3xl flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3 text-white">
                                <AlertTriangle className="w-5 h-5" />
                                <div>
                                    <h2 className="text-lg font-bold">Record Wastage</h2>
                                    <p className="text-xs text-white/80">Floor: {floor.name}</p>
                                </div>
                            </div>
                            <button onClick={handleClose} className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white" type="button">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-5">
                            {/* Wastage Type */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    Wastage Type
                                </label>
                                <div className="grid grid-cols-1 gap-2">
                                    {WASTAGE_TYPES.map(type => (
                                        <button
                                            key={type.value}
                                            onClick={() => setWastageType(type.value)}
                                            className={clsx(
                                                'flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all',
                                                wastageType === type.value
                                                    ? 'border-rose-300 bg-rose-50'
                                                    : 'border-slate-200 hover:border-slate-300'
                                            )}
                                            type="button"
                                        >
                                            <div className={clsx(
                                                'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
                                                wastageType === type.value ? 'border-rose-500 bg-rose-500' : 'border-slate-300'
                                            )}>
                                                {wastageType === type.value && <div className="w-2 h-2 rounded-full bg-white" />}
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-slate-700">{type.label}</p>
                                                <p className="text-xs text-slate-400">{type.description}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Product Search */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    Product <span className="text-rose-500">*</span>
                                </label>
                                {selectedProduct ? (
                                    <div className="flex items-center gap-2 p-3 border-2 border-emerald-200 bg-emerald-50/30 rounded-xl">
                                        <Package className="w-4 h-4 text-emerald-600" />
                                        <span className="text-sm font-semibold text-emerald-700">{selectedProduct.name}</span>
                                        <button
                                            onClick={() => { setSelectedProduct(null); setProductSearch(''); }}
                                            className="ml-auto p-1 text-slate-400 hover:text-rose-500"
                                            type="button"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search product..."
                                            value={productSearch}
                                            onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                                            onFocus={() => setShowProductDropdown(true)}
                                            className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400"
                                        />
                                        {showProductDropdown && productResults && productResults.length > 0 && (
                                            <div className="absolute top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl z-20 max-h-48 overflow-y-auto">
                                                {productResults.map((p) => (
                                                    <button
                                                        key={p.id}
                                                        type="button"
                                                        onClick={() => selectProduct(p)}
                                                        className="w-full text-left px-4 py-2.5 hover:bg-rose-50 text-sm border-b last:border-0"
                                                    >
                                                        <p className="font-medium truncate">{p.name}</p>
                                                        <p className="text-xs text-slate-400">{p.sku || p.brand}</p>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Quantity & Cost */}
                            {selectedProduct && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                            Quantity <span className="text-rose-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            step="0.001"
                                            min="0.001"
                                            value={quantity}
                                            onChange={(e) => setQuantity(e.target.value)}
                                            placeholder="0.000"
                                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400"
                                        />
                                        <p className="text-xs text-slate-400 mt-1">{uomName}</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                            Cost Value (₦)
                                        </label>
                                        <div className="relative">
                                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={costValue}
                                                onChange={(e) => setCostValue(e.target.value)}
                                                placeholder={selectedProduct.costPrice || '0.00'}
                                                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Estimated Loss */}
                            {selectedProduct && quantity && (
                                <div className="p-4 bg-rose-50 rounded-2xl border border-rose-200">
                                    <p className="text-xs text-rose-500 uppercase font-bold">Estimated Loss</p>
                                    <p className="text-2xl font-black text-rose-700">
                                        ₦{estimatedCost.toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                                    </p>
                                </div>
                            )}

                            {/* Reason */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    Reason <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="e.g., Expired, Burnt during cooking, Dropped..."
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400"
                                />
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    Notes (Optional)
                                </label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={2}
                                    placeholder="Additional details..."
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400"
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
                            <button
                                onClick={handleClose}
                                className="px-5 py-2.5 border-2 border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50"
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting || !selectedProduct || !quantity || !reason.trim()}
                                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50"
                                type="button"
                            >
                                {isSubmitting ? (
                                    <Spinner size="xs" variant="white" />
                                ) : (
                                    <AlertTriangle className="w-4 h-4" />
                                )}
                                Record Wastage
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}